/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash.debounce';
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { useSWRConfig } from 'swr';
import * as z from 'zod';

import { TestSuite } from '@/commons/protocols';
import Toggle from '@/components/atoms/toggle/Toggle';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { useGet, useMutate } from '@/net/commons';
import { useCheckOrg } from '@/net/commons';

const nameSchema = z.object({
    name: z.string().min(1, { message: 'Name is too short' }),
});
const urlSchema = z.object({
    defaultEntryHref: z.union([z.string().url({ message: 'Invalid URL format' }).min(1), z.literal('')]),
});

const SuiteSettings = () => {
    const navigate = useNavigate();
    const { projectSlug, suiteSlug } = useParams();
    const [isToggled, setIsToggled] = useState<boolean | null>(null);
    const [suiteName, setSuiteName] = useState<string | null>(null);
    const [entryURL, setEntryURL] = useState<string>('https://');
    const [isEntryURLSet, setIsEntryURLSet] = useState<boolean>(false);
    const { organisation } = useCheckOrg();
    const {
        data: settting,
        error,
        isLoading: isSettingLoading,
    } = useGet<TestSuite>(`/bff/project/${projectSlug}/suite/${suiteSlug}/settings`);
    const { mutate, cache } = useSWRConfig();
    const { trigger } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/settings`, {
        revalidate: true,
        method: 'PATCH',
        onSuccess: (data: any, key: string) => {
            const keyToUpdate = `/bff/project/${projectSlug}/suites`;
            const cachedData = cache.get(keyToUpdate)?.data;
            if (cachedData) {
                const updatedData = cachedData.map((item: any) => (item.id === data.id ? { ...item, ...data } : item));
                mutate(keyToUpdate, updatedData);
            }
        },
    });
    const { trigger: triggerRemove } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/settings/entrypoint`, {
        revalidate: true,
        method: 'DELETE',
    });

    const formName = useForm<z.infer<typeof nameSchema>>({
        defaultValues: {
            name: '',
        },
        resolver: zodResolver(nameSchema),
    });
    const formUrl = useForm<z.infer<typeof urlSchema>>({
        defaultValues: {
            defaultEntryHref: '',
        },
        resolver: zodResolver(urlSchema),
    });
    useEffect(() => {
        if (settting?.triggerNotification !== undefined) {
            setIsToggled(settting.triggerNotification);
        }
        if (settting?.name !== undefined) {
            setSuiteName(settting.name);
        }
        if (
            settting?.defaultEntryHref !== undefined &&
            settting.defaultEntryHref !== null &&
            settting.defaultEntryHref !== ''
        ) {
            setIsEntryURLSet(true);
            setEntryURL(settting.defaultEntryHref);
        }
    }, [settting]);

    const handleToggleChange = async () => {
        const newToggleState = !isToggled;
        setIsToggled(newToggleState);
        try {
            await trigger({ triggerNotification: newToggleState });
            toast({
                title: `Notify in Discord is ${newToggleState ? 'enabled' : 'disabled'}`,
            });
        } catch (err) {
            toast({
                title: `Error updating Discord notification`,
                variant: 'destructive',
            });
        }
    };
    const onNameSubmit = async (values: z.infer<typeof nameSchema>) => {
        try {
            await trigger({ name: values.name });
            if (values.name) {
                setSuiteName(values.name);
            }
            navigate(`/suite/${projectSlug}/${values.name}/settings`);
            toast({
                title: `Test suite renamed to ${values.name}`,
            });
        } catch (err) {
            const errorNew = err as { info?: { error: string; code: string }; status: number };
            if (errorNew.status === 409) {
                toast({
                    title: `Suite name already exists. Please choose a different name.`,
                    variant: 'destructive',
                });
                return;
            }
            toast({
                title: `Error renaming test suite`,
                variant: 'destructive',
            });
        }
    };
    const onUrlSubmit = async (values: z.infer<typeof urlSchema>) => {
        try {
            await trigger({ defaultEntryHref: values.defaultEntryHref });
            if (values.defaultEntryHref) {
                setIsEntryURLSet(true);
                setEntryURL(values.defaultEntryHref);
            }
            toast({
                title: `Entry URL changed to ${values.defaultEntryHref}`,
            });
        } catch (err) {
            toast({
                title: `Error changing Entry URL`,
                variant: 'destructive',
            });
        }
    };

    const handleNameSubmit = async (data: any) => {
        await onNameSubmit(data);
        formName.reset();
    };
    const handleUrlSubmit = async (data: any) => {
        await onUrlSubmit(data);
        formUrl.reset();
    };

    const removeDefaultEntrypoint = async () => {
        try {
            await triggerRemove({});
            setEntryURL('https://');
            formUrl.reset();
            setIsEntryURLSet(false);
            toast({
                title: 'The default entrypoint has been removed successfully.',
            });
        } catch (err) {
            toast({
                title: `There was a problem while removing the default entrypoint`,
                variant: 'destructive',
            });
        }
    };

    const [errorMessage, setErrorMessage] = useState('');
    const [shouldCheckName, setShouldCheckName] = useState(false);
    const { getValues } = formName;
    const { data: nameIsAvailable } = useGet<any>(
        shouldCheckName && getValues('name').length > 0
            ? `/bff/project/${projectSlug}/suites/${encodeURIComponent(getValues('name'))}/check`
            : null,
        {
            method: 'GET',
        },
    );

    useEffect(() => {
        if (shouldCheckName && nameIsAvailable && nameIsAvailable?.available === false) {
            setErrorMessage('This name already exists.');
        } else {
            setErrorMessage('');
        }
    }, [nameIsAvailable, shouldCheckName]);

    // debounce time to not dos the server
    const handleInputChange = useCallback(
        debounce(() => {
            if (getValues('name').length > 0) {
                setShouldCheckName(true);
            }
        }, 300), // 300ms debounce time
        [],
    );

    if (error) {
        return <div>Error loading settings</div>;
    }

    if (isToggled === null) {
        return <div>Loading...</div>; // Show a loading state until data is fetched
    }
    return (
        <div className="sm:max-w-6xl mx-auto">
            <h1 className="text-2xl font-medium text-gray-800">
                Settings for suite{' '}
                <span className="text-gray-500 font-extralight">
                    {' '}
                    {projectSlug}/{suiteSlug}
                </span>
            </h1>
            <div className="rounded-xl border p-6 space-y-8">
                <div className="space-y-3">
                    <div className="flex flex-row items-center gap-6">
                        <div>
                            <div>Notify</div>
                            <div className="text-xs text-muted-foreground">
                                Get a Discord notification when this suite finishes executing
                            </div>
                        </div>
                        <div className="flex h-full place-self-auto">
                            <Toggle defaultValue={isToggled} onClick={handleToggleChange} />
                        </div>
                    </div>
                    {!organisation?.discordWebhookUrl && (
                        <div className="border border-red-700 bg-red-200 rounded-md p-3">
                            Notifications are enabled by default. To start receiving them, please set up a Discord
                            webhook in{' '}
                            <Link to="/organisation/notification" className="underline">
                                Settings
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-6" />
            <div className="rounded-xl border p-6 space-y-8">
                <div className="space-y-3">
                    <Form {...formName}>
                        <form onSubmit={formName.handleSubmit(handleNameSubmit)} className="flex flex-col gap-10">
                            <FormField
                                control={formName.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex flex-row gap-2 items-center">
                                            Test Suite Name
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={`${suiteName}`}
                                                {...field}
                                                onInput={(e) => {
                                                    setErrorMessage('');
                                                    setShouldCheckName(false);
                                                    field.onChange(e);
                                                    handleInputChange();
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage>{errorMessage && errorMessage}</FormMessage>
                                    </FormItem>
                                )}
                            />
                            <div className="flex flex-row justify-end">
                                <Button
                                    type="submit"
                                    disabled={
                                        isSettingLoading || (nameIsAvailable && nameIsAvailable?.available === false)
                                    }
                                >
                                    Change
                                </Button>
                            </div>
                        </form>
                    </Form>
                    <Form {...formUrl}>
                        <form onSubmit={formUrl.handleSubmit(handleUrlSubmit)} className="flex flex-col gap-10">
                            <FormField
                                control={formUrl.control}
                                name="defaultEntryHref"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex flex-row gap-2 items-center">
                                            Default Entrypoint URL
                                        </FormLabel>
                                        <FormControl>
                                            <Input placeholder={`${entryURL}`} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex flex-row justify-end gap-x-4">
                                {isEntryURLSet ? (
                                    <Button
                                        type="submit"
                                        disabled={
                                            isSettingLoading || entryURL === String(formUrl.watch('defaultEntryHref'))
                                        }
                                    >
                                        Change
                                    </Button>
                                ) : (
                                    <Button type="submit" disabled={isSettingLoading}>
                                        {' '}
                                        Set{' '}
                                    </Button>
                                )}

                                {isEntryURLSet && (
                                    <Button
                                        type="submit"
                                        onClick={() => {
                                            removeDefaultEntrypoint();
                                        }}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default SuiteSettings;
